
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import PageContentBase from '@/layout/_shared/PageContentBase.vue';
import PageContentSection from '@/layout/_shared/PageContentSection.vue';
import DivRow from '@/layout/_shared/DivRow.vue';
import DivCol from '@/layout/_shared/DivCol.vue';
import LabelCustom from '@/components/shared/label-custom/LabelCustom.vue';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import useCapDonVi from '@/core/hooks/use-cap-don-vi';
import { DonViService } from '@/core/services/DonVi.service';
import { CaiDatService } from '@/core/services/CaiDat.service';
import {
  ICaiDatItem,
  IDonViResponseBase,
} from '@/core/interfaces/ApiResponses';
import {
  formatGiaTriDisplay,
  loaiGiaTriLabel,
  normalizeCaiDatItem,
} from '@/core/helpers/cai-dat';
import {
  clampYearToWorkingYears,
  extractWorkingYears,
} from '@/core/helpers/working-years';
import {
  hasAdminRole,
  hasSuperadminRole,
} from '@/core/helpers/authorization-utils';
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from '@/core/helpers/breadcrumb';
import { swalErrNotification } from '@/core/helpers/utils';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import CaiDatCreateModal from './CaiDatCreateModal.vue';
import CaiDatEditModal from './CaiDatEditModal.vue';
import Swal from 'sweetalert2';

type CaiDatFilter = 'all' | 'editable' | 'readonly';

export default defineComponent({
  name: 'cau-hinh-he-thong',

  components: {
    PageContentBase,
    PageContentSection,
    DivRow,
    DivCol,
    LabelCustom,
    ButtonCustom,
    CaiDatCreateModal,
    CaiDatEditModal,
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const { ButtonsType, ButtonTypeColors } = useButtonCustom();
    const { isCompanyLevel } = useCapDonVi();

    const departmentOptions = ref<IDonViResponseBase[]>([]);
    const isSaving = ref(false);

    const form = ref({
      year: store.getters.getSelectedYear || new Date().getFullYear().toString(),
      idDonVi: store.getters.getSystemIdDonVi as number | null,
      perpage: store.getters.getSystemPerPage as number,
    });

    const actorRoleNames = computed<string[]>(
      () =>
        store.getters.currentUser?.roles?.map(
          (role: { name: string }) => role.name
        ) || []
    );

    const isSuperadmin = computed(() =>
      hasSuperadminRole(actorRoleNames.value)
    );
    const canEditCaiDat = computed(() => hasAdminRole(actorRoleNames.value));

    const caiDatItems = ref<ICaiDatItem[]>([]);
    const caiDatLoading = ref(false);
    const caiDatFilter = ref<CaiDatFilter>('all');
    const showAllDonVi = ref(false);

    const createDialogVisible = ref(false);
    const editDialogVisible = ref(false);
    const editingItem = ref<ICaiDatItem | null>(null);

    const workingYears = computed<number[]>(
      () => store.getters.getWorkingYears as number[]
    );

    const filteredCaiDatItems = computed(() => {
      let items = [...caiDatItems.value];

      if (caiDatFilter.value === 'editable') {
        items = items.filter((item) => !item.is_readonly);
      } else if (caiDatFilter.value === 'readonly') {
        items = items.filter((item) => item.is_readonly);
      }

      if (showAllDonVi.value) {
        items.sort((a, b) => {
          if (a.id_don_vi !== b.id_don_vi) {
            return a.id_don_vi - b.id_don_vi;
          }
          return a.khoa.localeCompare(b.khoa);
        });
      } else {
        items.sort((a, b) => a.khoa.localeCompare(b.khoa));
      }

      return items;
    });

    const loadDepartments = async () => {
      try {
        const {
          data: { data },
        } = await DonViService.fetchAllDepartments();
        departmentOptions.value = data;
      } catch (error) {
        await swalErrNotification(error, 'Có lỗi khi tải danh sách đơn vị');
      }
    };

    const fetchCaiDat = async () => {
      caiDatLoading.value = true;
      try {
        const { data: responseBody } = await CaiDatService.list({
          all: isSuperadmin.value && showAllDonVi.value,
        });
        const data = responseBody.data;
        caiDatItems.value = Array.isArray(data)
          ? data.map(normalizeCaiDatItem)
          : [];

        const years = extractWorkingYears(responseBody);
        if (years?.length) {
          store.commit(Mutations.SET_WORKING_YEARS, years);
          form.value.year = clampYearToWorkingYears(form.value.year, years);
        }
      } catch (error) {
        caiDatItems.value = [];
        await swalErrNotification(error, 'Có lỗi khi tải danh sách cài đặt');
      } finally {
        caiDatLoading.value = false;
      }
    };

    const onSubmit = async () => {
      isSaving.value = true;

      try {
        const year = clampYearToWorkingYears(
          form.value.year,
          workingYears.value
        );

        await store.dispatch(Actions.SAVE_USER_SYSTEM_SETTINGS, {
          year,
          idDonVi: form.value.idDonVi,
          perpage: form.value.perpage,
        });

        await Swal.fire({
          icon: 'success',
          title: 'Thành công',
          text: 'Đã cập nhật cấu hình hệ thống',
          timer: 1500,
          showConfirmButton: false,
        });

        router.go(0);
      } catch (error) {
        await swalErrNotification(error, 'Có lỗi khi lưu cấu hình');
      } finally {
        isSaving.value = false;
      }
    };

    const openEditDialog = (item: ICaiDatItem) => {
      editingItem.value = normalizeCaiDatItem(item);
      editDialogVisible.value = true;
    };

    const closeEditDialog = () => {
      editDialogVisible.value = false;
      editingItem.value = null;
    };

    onMounted(async () => {
      setCurrentPageTitle('Cấu hình');
      setBreadcrumbLinks(
        [
          {
            text: 'Hệ thống',
          },
          {
            text: 'Cấu hình',
          },
        ],
        true
      );
      document.title = 'Cấu hình - Đường sắt Hà Nội';

      await loadDepartments();

      form.value = {
        year: store.getters.getSelectedYear || new Date().getFullYear().toString(),
        idDonVi:
          store.getters.getSystemIdDonVi ??
          store.getters.currentUser?.departmentId ??
          null,
        perpage: store.getters.getSystemPerPage,
      };

      await fetchCaiDat();
    });

    return {
      form,
      ButtonsType,
      ButtonTypeColors,
      workingYears,
      departmentOptions,
      isCompanyLevel,
      isSaving,
      onSubmit,
      isSuperadmin,
      canEditCaiDat,
      caiDatLoading,
      caiDatFilter,
      showAllDonVi,
      filteredCaiDatItems,
      fetchCaiDat,
      loaiGiaTriLabel,
      formatGiaTriDisplay,
      createDialogVisible,
      editDialogVisible,
      editingItem,
      openEditDialog,
      closeEditDialog,
    };
  },
});
